import { useEffect, useState } from "react";
import {
  buildOwnCustomerFormData,
  getDefaultEntity,
  ownCustomerInitialValues,
  ownCustomerValidationSchema,
} from "../../../../utils/helpers";
import { useFormik } from "formik";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import BaseCard from "../../../../components/ui/BaseCard";
import BaseButton from "../../../../components/ui/BaseButton";
import BaseLoader from "../../../../components/ui/BaseLoader";
import {
  AddressFields,
  ClientFormGrid,
  ClientPhoneField,
  AnnualRevenueField,
  ContactPersonField,
  EmailField,
  LogoField,
  NameField,
  RegistrationCertificateField,
  RegistrationNumberField,
  WebsiteField,
  useClientFormContext,
} from "../../../Client/CreateEditClient/CreateEditClientForm";
import { getClientById, updateOwnCustomerProfile } from "../../../../services/api/client";
import { showToast } from "../../../../store/toast/toastSlice";

const validationSchema = ownCustomerValidationSchema;

// Named the way the side-nav tooltip lists them.
const REQUIRED_FIELDS = [
  ["clientName", "Company Name"],
  ["address", "Address"],
  ["city", "City"],
  ["zipCode", "Zip / Post Code"],
  ["country", "Country"],
  ["phone", "Phone"],
  ["email", "Email"],
  ["companyAnnualRevenue", "Annual Revenue"],
  ["manager", "Contact Person"],
  ["registrationNumber", "Registration Number"],
  ["registrationCertificate", "Registration Certificate"],
];

const missingFieldsOf = (values) =>
  REQUIRED_FIELDS.filter(([key]) => !String(values?.[key] ?? "").trim()).map(([, label]) => label);

const CustomerProfileStep = ({ customerId, onDirtyChange = null, onCompletionChange = null }) => {
  const dispatch = useDispatch();
  const [isEditable, setIsEditable] = useState(false);

  const {
    data: clientData,
    isPending: isClientLoading,
    refetch: refetchClient,
  } = useQuery({
    queryKey: ["client", String(customerId)],
    queryFn: () => getClientById(customerId),
    enabled: !!customerId,
  });

  const { isPending: isSaving, mutateAsync: saveProfile } = useMutation({
    mutationFn: updateOwnCustomerProfile,
  });

  const customer = clientData?.data;
  const entity = getDefaultEntity(customer);
  const hasData = !!(
    customer?.address?.addressLine1 ||
    customer?.address?.city ||
    customer?.policies ||
    entity?.managerName ||
    entity?.registrationNumber ||
    entity?.registrationCertificate
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: ownCustomerInitialValues(customer),
    validationSchema,
    onSubmit: (values) => handleSave(values),
  });

  const formCtx = useClientFormContext({
    formik,
    isEditable: isEditable,
    lockedFields: ["clientName", "phone", "email"],
  });

  const handleSave = async (values) => {
    try {
      const formData = buildOwnCustomerFormData(values);

      await saveProfile(formData);
      dispatch(showToast({ message: "Company updated successfully", type: "success" }));
      await refetchClient();
      setIsEditable(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to update company",
          type: "error",
        }),
      );
    }
  };

  // What the side-nav step needs: which required details are still missing on the
  // saved record, and whether there are unsaved edits.
  useEffect(() => {
    if (!customer) return;
    onCompletionChange?.(
      missingFieldsOf({
        clientName: customer.clientName,
        address: customer.address?.addressLine1,
        city: customer.address?.city,
        zipCode: customer.address?.zipCode,
        country: customer.address?.country,
        phone: customer.phoneNumber,
        email: customer.email,
        companyAnnualRevenue: customer.companyAnnualRevenue,
        manager: getDefaultEntity(customer)?.managerName,
        registrationNumber: getDefaultEntity(customer)?.registrationNumber,
        registrationCertificate: getDefaultEntity(customer)?.registrationCertificate?.fileName,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  useEffect(() => {
    onDirtyChange?.(formik.dirty);
  }, [formik.dirty, onDirtyChange]);

  useEffect(() => {
    if (customer && !hasData) {
      setIsEditable(true);
    }
  }, [customer, hasData]);

  if (!customerId) {
    return <p className="py-4">No customer record is linked to this account.</p>;
  }

  if (isClientLoading) {
    return (
      <div className="flex justify-center py-10">
        <BaseLoader isLoading={isClientLoading} />
      </div>
    );
  }

  return (
    <div>
      <BaseCard elevation={0} className="py-4 px-5 rounded-xl shadow-lg bg-transparent">
        <div className="flex justify-between w-full">
          <div className="hidden sm:block w-2/6"></div>
          <div className="sm:col-span-4 col-span-7 flex items-center justify-end gap-3">
            {!isEditable && hasData ? (
              <BaseButton
                variant="contained"
                color="primary"
                onClick={() => setIsEditable(true)}
                className="py-[5px] whitespace-nowrap"
              >
                Edit Company Details
              </BaseButton>
            ) : (
              <>
                {formik.dirty && (
                  <p className="text-amber-600 text-sm font-normal self-center">
                    You have unsaved changes — click Update to apply.
                  </p>
                )}
                {hasData && (
                  <BaseButton
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      formik.resetForm();
                      setIsEditable(false);
                    }}
                    className="py-[5px]"
                  >
                    Cancel
                  </BaseButton>
                )}
                <BaseButton
                  loaderSize="24px"
                  isLoading={isSaving}
                  disabled={isSaving || !formik.dirty}
                  variant="contained"
                  color="primary"
                  onClick={formik.handleSubmit}
                  className="py-[5px]"
                >
                  Update
                </BaseButton>
              </>
            )}
          </div>
        </div>
      </BaseCard>

      <ClientFormGrid ctx={formCtx} compactTop>
        <NameField ctx={formCtx} />
        <EmailField ctx={formCtx} />
        <ClientPhoneField ctx={formCtx} />
        <ContactPersonField ctx={formCtx} />
        <RegistrationNumberField ctx={formCtx} />
        <RegistrationCertificateField ctx={formCtx} />
        <LogoField ctx={formCtx} />
        <AddressFields ctx={formCtx} />
        <WebsiteField ctx={formCtx} />
      </ClientFormGrid>
    </div>
  );
};

export default CustomerProfileStep;
